import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CaseDetails, StiiraError } from '@core/models';
import { EmployeeLeaveHoursExtended, LeaveCalendarHoursForm, LeaveCalendarHoursPost, WorkCalendarDay } from '@core/models/leave-admin/leave-calendar/leave-calendar.model';
import { LayoutService, ErrorService } from '@core/services';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { EventApi } from '@fullcalendar/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { Subject, Observable } from 'rxjs';
import { DeleteConfirmationComponent } from '../delete-confirmation/delete-confirmation.component';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { takeUntil } from 'rxjs/operators';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';

@Component({
    selector: 'app-edit-calendar-time',
    templateUrl: './edit-calendar-time.component.html',
    styleUrls: ['./edit-calendar-time.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditCalendarTimeComponent implements OnInit {
  static readonly componentName = 'EditCalendarTimeComponent';
  
  public form: FormGroup<LeaveCalendarHoursForm>;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  // save and delete share isSubmitting$, so this says which one is running and the template puts the spinner in that button
  public submittingAction: 'save' | 'delete';
  public showOtherLeavesWarn: boolean = false;
  public showFullDayControl: boolean = false;
  public showFullDayWarning: boolean = false;
  public showAllBanksFullDayIncRestrictHint: boolean = false;
  public showSomeBanksFullDayIncRestrictHint: boolean = false;
  public fullDayIncrement: number = null;
  public position = position;
  public onDragEnded = onDragEnded;

  private formInitValues: any;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value) === JSON.stringify(this.formInitValues);
  }

  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { 
      event: EventApi,
      caseDetails: CaseDetails,
      hoursExtended: EmployeeLeaveHoursExtended,
      workCalendarDay: WorkCalendarDay,
      sysText: any,
      unsavedChangesSystext: any
    },
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditCalendarTimeComponent>,
    private dialog: MatDialog,
    private layoutService: LayoutService,
    private errorService: ErrorService,
    private service: LeaveAdminService,
    private store: LeaveAdminStoreService
  ) { 
    this.form = this.fb.group<LeaveCalendarHoursForm>({
      hours: this.fb.control(null, [Validators.required, Validators.pattern(/^\s*(?=.*[0-9])\d*(?:\.\d{1,2})?\s*$/)]),
      isFullDay: this.fb.control(false)
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close(false));

    this.setupForm();
    this.showOtherLeavesWarn = (this.data.hoursExtended?.leaveCaseHoursID == null 
      && this.data.hoursExtended?.leavesCount > 0) 
      || (this.data.hoursExtended?.leaveCaseHoursID != null 
      && this.data.hoursExtended?.leavesCount > 1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  public onSubmit(): void {
    this.submittingAction = 'save';
    if (this.store.isActionRunning) {
      const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px'
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.onSubmit();
      }
    });
      return;
    }

    const dto: LeaveCalendarHoursPost = {
      caseId: this.data.caseDetails.leaveInformation.caseId,
      hoursId: this.data.hoursExtended.leaveCaseHoursID,
      calendarDay: this.data.event.start,
      hours: this.form.controls.hours.value
    };
    
    this.service.postLeaveCalendarHours(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res)=>{
        this.dialogRef.close(res);
    },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)); 
  }

  public onDelete(): void {
    this.submittingAction = 'delete';
    const dialogConfig: MatDialogConfig = {
      id: DeleteConfirmationComponent.componentName,
      width: '300px',
      disableClose: false,
      data: { sysText: this.store.sysText.deleteConfirmation }
    };

    this.dialog.open(DeleteConfirmationComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
        if (res) {
          if (this.store.isActionRunning) {
              const dialogConfig: MatDialogConfig = {
              id: PendingActionsAlertComponent.componentName,
              width: '300px'
            };
            this.dialog.open(PendingActionsAlertComponent, dialogConfig)
              .beforeClosed()
              .subscribe((res: boolean) => {
              if (res) {
                this.onDelete();
              }
            });
            return;
          }

          this.service.postLeaveCalendarDayDelete({id: this.data.hoursExtended.leaveCaseHoursID})
            .pipe(indicate(this.isSubmitting$))
            .subscribe(res => {
              this.dialogRef.close(res)
            });
        }
      });
  }

  private setupForm(): void {
    this.fullDayIncrement = this.data.workCalendarDay?.fullDayIncrement ?? null;
    const li = this.data.caseDetails.leaveInformation;
    const leaveCaseHours = this.data.hoursExtended.leaveCaseLeaveHours;

    const someBanksRestrict = (
      li.bank1RestrictToFullDayIncrements
      || li.bank2RestrictToFullDayIncrements
      || li.bank3RestrictToFullDayIncrements
      || li.bank4RestrictToFullDayIncrements
      || li.bank5RestrictToFullDayIncrements);

    const allBanksRestrict = (
      li.isBank1HoursApplicable
      || li.isBank2HoursApplicable
      || li.isBank3HoursApplicable
      || li.isBank4HoursApplicable
      || li.isBank5HoursApplicable)
      && (!li.isBank1HoursApplicable || li.bank1RestrictToFullDayIncrements)
      && (!li.isBank2HoursApplicable || li.bank2RestrictToFullDayIncrements)
      && (!li.isBank3HoursApplicable || li.bank3RestrictToFullDayIncrements)
      && (!li.isBank4HoursApplicable || li.bank4RestrictToFullDayIncrements)
      && (!li.isBank5HoursApplicable || li.bank5RestrictToFullDayIncrements);

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.checkHours(allBanksRestrict);
      });

    // if banks restrict and workday hours exist, listen to hours changes 
    // and 'check' 'is full workday' if hours are equal to full workday hours
    if (someBanksRestrict && this.fullDayIncrement != null) {
      this.form.controls.hours.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res)=>{
          if (res == this.fullDayIncrement) {
            this.form.controls.isFullDay.setValue(true, {emitEvent: false});
          } else if (this.form.controls.isFullDay.value) {
            this.form.controls.isFullDay.setValue(false, {emitEvent: false});
          }
        });
    }

    // if there are existing hours set value
    if (this.data.hoursExtended?.leaveCaseLeaveHours) {
      this.form.patchValue({
        hours: this.data.hoursExtended.leaveCaseLeaveHours
      });
    }

    // if at least some banks restrict, show full day checkbox
    if (someBanksRestrict) {
      this.showFullDayControl = true;
      // ...if work schedule missing or no actual workday hours disable 
      // checkbox (non-workday), disable checkbox
      if (!this.data.workCalendarDay || !this.fullDayIncrement) {
        this.form.controls.isFullDay.disable();
      // ...if no actual workday hours disable checkbox (non-workday)
      }
    }

    // handle add hours (no leave case hours) when all banks restrict
    if (!leaveCaseHours && allBanksRestrict) {
      // ...if all banks restrict and no full day icrement found 
      // (no shcedule) show full day warning
      if (!this.fullDayIncrement) {
        this.showFullDayWarning = true;
      }
      // ...otherwise set values to full workday hours
      else {
        this.form.patchValue({
          hours: this.fullDayIncrement,
          isFullDay: true
        });
      }
    }

    // show all/some banks restrict hints
    if (allBanksRestrict) {
      this.showAllBanksFullDayIncRestrictHint = true;
    } else if (someBanksRestrict) {
      this.showSomeBanksFullDayIncRestrictHint = true;
    }
    
    // checking 'full workday' sets hours to workday hours
    this.form.controls.isFullDay.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res)=>{
        if (res && this.fullDayIncrement) {
          this.form.controls.hours.setValue(this.fullDayIncrement, {emitEvent: false});
        } else if (!res && this.fullDayIncrement == this.form.controls.hours.value) {
          this.form.controls.isFullDay.setValue(true, {emitEvent: false});
        }
      });

    this.formInitValues = { ...this.form.value };
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSystext
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private checkHours(allBanksRestrict: boolean): void {
    if ((this.form.controls.hours.value > 24)) {
      this.form.controls.hours.setErrors({ 'hoursGreaterThan24': true });
    };

    if (allBanksRestrict 
      && !!this.form.controls.hours.value
      && (!this.data.workCalendarDay?.fullDayIncrement 
      || this.form.controls.hours.value != this.data.workCalendarDay?.fullDayIncrement)) {
      this.showFullDayWarning = true;
    } else {
      this.showFullDayWarning = false;
    }
  }
}
